'use client'

import { useEffect, useState } from 'react'
import { twMerge } from 'tailwind-merge'

interface InputCounterProps {
  initValue: number | null
  label?: string
  minValue?: number
  maxValue?: number
  updateValue: (age: number) => void
  containerClassName?: string
  btnClassName?: string
}

export default function InputDropdown({
  initValue,
  label,
  minValue = 0,
  maxValue = 99,
  updateValue,
  containerClassName,
  btnClassName,
}: InputCounterProps) {
  const [value, setValue] = useState<number | null>(initValue)
  const [disableIncrement, setDisableIncrement] = useState<boolean>(false)
  const [disableDecrement, setDisableDecrement] = useState<boolean>(false)

  useEffect(() => {
    setDisableDecrement(value === minValue)
    setDisableIncrement(value === maxValue)

    if (value !== null) updateValue(value)
  }, [value])

  const handleChange = (isIncreased: boolean) => {
    setValue((prev) => {
      if (isIncreased) {
        return maxValue
          ? Math.min(Number(prev) + 1, maxValue)
          : Number(prev) + 1
      } else {
        return Math.max(Number(prev) - 1, minValue)
      }
    })
  }

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value

    if (/^\d*$/.test(newValue)) {
      const numericValue = Number(newValue)
      if (numericValue >= minValue && numericValue <= maxValue) {
        setValue(numericValue)
      } else if (newValue === '') {
        setValue(minValue)
      }
    }
  }

  return (
    <div
      className={twMerge('relative w-40 flex items-center', containerClassName)}
    >
      <button
        onClick={() => handleChange(false)}
        type="button"
        disabled={disableDecrement}
        className={twMerge(
          `${disableDecrement ? 'opacity-50 cursor-not-allowed' : ''} bg-gray-100 hover:bg-gray-200 border border-gray-300 rounded-s-lg p-3 w-9 h-11`,
          btnClassName
        )}
      >
        <svg
          className="w-3 h-3 text-gray-900 dark:text-white"
          aria-hidden="true"
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 18 2"
        >
          <path
            stroke="currentColor"
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="2"
            d="M1 1h16"
          />
        </svg>
      </button>
      <input
        type="text"
        id="bedrooms-input"
        data-input-counter
        data-input-counter-min="1"
        data-input-counter-max="5"
        className={twMerge(
          'bg-white-50 border-x-0 border-gray-300 h-11 font-medium text-center text-gray-900 text-sm block w-full',
          label && 'pb-6',
          btnClassName
        )}
        placeholder=""
        value={value ?? '-'}
        onChange={(e) => handleInputChange(e)}
        required
      />
      {label && (
        <div className="absolute bottom-1 start-1/2 -translate-x-1/2 rtl:translate-x-1/2 flex items-center text-xs text-gray-400 space-x-1 rtl:space-x-reverse">
          <svg
            className="w-2.5 h-2.5 text-gray-400"
            aria-hidden="true"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 20 20"
          >
            <path
              stroke="currentColor"
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              d="M3 8v10a1 1 0 0 0 1 1h4v-5a1 1 0 0 1 1-1h2a1 1 0 0 1 1 1v5h4a1 1 0 0 0 1-1V8M1 10l9-9 9 9"
            />
          </svg>
          <span>{label}</span>
        </div>
      )}
      <button
        onClick={() => handleChange(true)}
        type="button"
        disabled={disableIncrement}
        className={twMerge(
          `${disableIncrement ? 'opacity-50' : ''} bg-gray-100 hover:bg-gray-200 border border-gray-300 rounded-e-lg p-3 w-9 h-11`,
          btnClassName
        )}
      >
        <svg
          className="w-3 h-3 text-gray-900 dark:text-white"
          aria-hidden="true"
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 18 18"
        >
          <path
            stroke="currentColor"
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="2"
            d="M9 1v16M1 9h16"
          />
        </svg>
      </button>
    </div>
  )
}
